import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import QrScannerDialog from '@/components/QrScannerDialog';
import { Camera, Eye, EyeOff, QrCode, Terminal } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { buildAppUrl, cn, toQrDataUrl, validatePassword } from '@/lib/utils';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const LoginPage = ({ onLogin, embedded = false, initialTab = 'login' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const normalizedInitialTab = initialTab === 'register' ? 'register' : 'login';
  const [activeTab, setActiveTab] = useState(normalizedInitialTab);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [showForgotHint, setShowForgotHint] = useState(false);
  const [isForgotOpen, setIsForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isForgotSending, setIsForgotSending] = useState(false);
  const [twoFaLogin, setTwoFaLogin] = useState(null);
  const [twoFaCode, setTwoFaCode] = useState('');
  const [isTwoFaVerifying, setIsTwoFaVerifying] = useState(false);
  const [registerData, setRegisterData] = useState({ username: '', password: '', passwordConfirm: '', email: '' });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [isQrLoginOpen, setIsQrLoginOpen] = useState(false);
  const [qrLogin, setQrLogin] = useState(null);
  const [qrStatus, setQrStatus] = useState('idle');
  const [qrError, setQrError] = useState(null);
  const [isQrScanOpen, setIsQrScanOpen] = useState(false);
  const [isQrScanExchanging, setIsQrScanExchanging] = useState(false);
  const pollTimerRef = useRef(null);
  const qrRefreshTimerRef = useRef(null);
  const qrCountdownTimerRef = useRef(null);
  const [qrRefreshInSeconds, setQrRefreshInSeconds] = useState(null);

  useEffect(() => {
    setActiveTab(normalizedInitialTab);
  }, [normalizedInitialTab]);

  const resendEmailConfirmation = async () => {
    const username = String(loginData.username || '').trim();
    const password = String(loginData.password || '');
    if (!username || !password) return;
    try {
      await axios.post(`${API}/auth/email/resend`, { username, password }, { withCredentials: true });
      toast.success('Письмо отправлено');
    } catch (error) {
      const status = error?.response?.status;
      if (status === 429) {
        const retryAfterRaw = error?.response?.headers?.['retry-after'];
        const retryAfterSeconds = Number.parseInt(String(retryAfterRaw || ''), 10);
        if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
          const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
          toast.error(`Слишком часто. Попробуйте через ${minutes} мин.`);
          return;
        }
        toast.error('Слишком часто. Попробуйте позже');
        return;
      }
      toast.error(error?.response?.data?.detail || 'Не удалось отправить письмо');
    }
  };

  const sendForgotPassword = async () => {
    const email = String(forgotEmail || '').trim();
    if (!email) {
      toast.error('Введите email');
      return;
    }
    if (!EMAIL_RE.test(email) || email.length > 254) {
      toast.error('Некорректный email');
      return;
    }
    setIsForgotSending(true);
    try {
      const resp = await axios.post(`${API}/auth/forgot-password`, { email });
      if (resp?.data?.exists === false) {
        toast.error('Аккаунта с такой почтой не существует');
        return;
      }
      toast.success('Письмо отправлено');
      setIsForgotOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Не удалось отправить письмо');
    } finally {
      setIsForgotSending(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const username = String(loginData.username || '').trim();
    const password = String(loginData.password || '');
    if (!username) {
      toast.error('Введите имя пользователя');
      return;
    }
    if (!password) {
      toast.error('Введите пароль');
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password }, { withCredentials: true });
      if (response?.data?.requires_2fa) {
        const loginId = response?.data?.login_id;
        if (typeof loginId !== 'string' || !loginId.trim()) {
          toast.error('Не удалось начать вход с 2FA');
          return;
        }
        setTwoFaLogin({
          login_id: loginId,
          expires_at: response?.data?.expires_at || null,
          ttl_seconds: response?.data?.ttl_seconds || null,
        });
        setTwoFaCode('');
        toast('Введите код 2FA', { duration: 2500 });
        return;
      }

      if (!response?.data?.ok) {
        toast.error('Ошибка входа');
        return;
      }
      await onLogin?.();
      toast.success('Успешный вход!');
    } catch (error) {
      const status = error?.response?.status;
      const authReason = error?.response?.headers?.['x-auth-reason'];
      if (status === 403 && authReason === 'email_unverified') {
        toast.error('Подтвердите email', {
          action: {
            label: 'Отправить ещё раз',
            onClick: resendEmailConfirmation,
          },
        });
        return;
      }
      if (status === 401) {
        setShowForgotHint(true);
        toast.error('Неверный логин или пароль');
        return;
      }
      if (status === 429) {
        const retryAfterRaw = error?.response?.headers?.['retry-after'];
        const retryAfterSeconds = Number.parseInt(String(retryAfterRaw || ''), 10);
        if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
          const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
          toast.error(`Слишком много попыток. Попробуйте через ${minutes} мин.`);
          return;
        }
        toast.error('Слишком много попыток. Попробуйте позже');
        return;
      }
      toast.error(error.response?.data?.detail || 'Ошибка входа');
    } finally {
      setIsLoading(false);
    }
  };

  const resetTwoFaLogin = () => {
    setTwoFaLogin(null);
    setTwoFaCode('');
    setIsTwoFaVerifying(false);
  };

  const handleLoginTwoFa = async (e) => {
    e.preventDefault();
    const loginId = typeof twoFaLogin?.login_id === 'string' ? twoFaLogin.login_id.trim() : '';
    const code = String(twoFaCode || '').trim();
    if (!loginId) {
      resetTwoFaLogin();
      toast.error('Сессия 2FA не найдена, попробуйте войти снова');
      return;
    }
    if (!code) {
      toast.error('Введите код');
      return;
    }
    setIsTwoFaVerifying(true);
    try {
      const response = await axios.post(`${API}/auth/login/2fa`, { login_id: loginId, code }, { withCredentials: true });
      if (!response?.data?.ok) {
        toast.error('Не удалось войти');
        return;
      }
      resetTwoFaLogin();
      await onLogin?.();
      toast.success('Успешный вход!');
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404 || status === 410) {
        resetTwoFaLogin();
      }
      toast.error(error.response?.data?.detail || 'Ошибка входа');
    } finally {
      setIsTwoFaVerifying(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const username = String(registerData.username || '').trim();
    const password = String(registerData.password || '');
    const passwordConfirm = String(registerData.passwordConfirm || '');
    const email = String(registerData.email || '').trim();
    if (!username) {
      toast.error('Введите имя пользователя');
      return;
    }
    const passErr = validatePassword(password);
    if (passErr) {
      toast.error(passErr);
      return;
    }
    if (password !== passwordConfirm) {
      toast.error('Пароли не совпадают');
      return;
    }
    if (!email) {
      toast.error('Введите email');
      return;
    }
    if (!EMAIL_RE.test(email) || email.length > 254) {
      toast.error('Некорректный email');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/register`, { username, password, email });
      toast.success('Регистрация успешна! Проверьте почту для активации.');
      setLoginData({ username, password: '' });
      setRegisterData({ username: '', password: '', passwordConfirm: '', email: '' });
      setShowRegisterPassword(false);
      setActiveTab('login');
    } catch (error) {
      if (username && password) {
        try {
          await axios.post(`${API}/auth/login`, { username, password }, { withCredentials: true });
          toast.success('Регистрация успешна!');
          setLoginData({ username, password: '' });
          setRegisterData({ username: '', password: '', passwordConfirm: '', email: '' });
          setShowRegisterPassword(false);
          setActiveTab('login');
          return;
        } catch {}
      }
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string' && detail.trim()) {
        toast.error(detail);
        return;
      }
      const status = error?.response?.status;
      const statusText = error?.response?.statusText;
      const fallback =
        typeof status === 'number'
          ? `Ошибка регистрации (${status}${statusText ? ` ${statusText}` : ''})`
          : (error?.message || 'Ошибка регистрации');
      toast.error(fallback);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'login') resetTwoFaLogin();
  }, [activeTab]);

  const parseOneTimeCodeFromValue = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return null;

    const parseFromSearchParams = (searchParams) => {
      const code = searchParams?.get?.('code');
      return code ? String(code).trim() : null;
    };

    try {
      const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
      const url = new URL(raw, base);
      const found = parseFromSearchParams(url.searchParams);
      if (found) return found;
    } catch {}

    if (raw.includes('code=')) {
      try {
        const idx = raw.indexOf('?');
        const query = idx >= 0 ? raw.slice(idx + 1) : raw;
        const sp = new URLSearchParams(query);
        const found = parseFromSearchParams(sp);
        if (found) return found;
      } catch {}
    }

    return null;
  };

  const handleScanLoginResult = async (value) => {
    const code = parseOneTimeCodeFromValue(value);
    if (!code) {
      toast.error('Некорректный QR');
      return;
    }
    setIsQrScanExchanging(true);
    try {
      const resp = await axios.post(`${API}/auth/one-time/exchange`, { code }, { withCredentials: true });
      if (!resp?.data?.ok) {
        toast.error('Не удалось войти');
        return;
      }
      await onLogin?.();
      toast.success('Успешный вход!');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Не удалось войти');
    } finally {
      setIsQrScanExchanging(false);
    }
  };

  const stopQrPolling = () => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (qrRefreshTimerRef.current) {
      window.clearTimeout(qrRefreshTimerRef.current);
      qrRefreshTimerRef.current = null;
    }
    if (qrCountdownTimerRef.current) {
      window.clearInterval(qrCountdownTimerRef.current);
      qrCountdownTimerRef.current = null;
    }
    setQrRefreshInSeconds(null);
  };

  const startQrLogin = async () => {
    stopQrPolling();
    setQrError(null);
    setQrStatus('starting');
    setQrLogin(null);
    try {
      const resp = await axios.post(`${API}/auth/qr/start`, undefined, { withCredentials: true });
      const qrId = resp?.data?.qr_id;
      const code = resp?.data?.code;
      const expiresAt = resp?.data?.expires_at || null;
      const ttlSeconds = resp?.data?.ttl_seconds || null;
      if (!qrId || !code) {
        setQrStatus('error');
        setQrError('Не удалось создать QR');
        return;
      }
      const url = buildAppUrl(`/qr?qr_id=${encodeURIComponent(String(qrId))}&code=${encodeURIComponent(String(code))}`);
      const dataUrl = await toQrDataUrl(url, { width: 260, margin: 2 });
      setQrLogin({ qr_id: qrId, code, url, expires_at: expiresAt, ttl_seconds: ttlSeconds, data_url: dataUrl });
      setQrStatus('pending');
    } catch (error) {
      setQrStatus('error');
      setQrError(error?.response?.data?.detail || 'Не удалось создать QR');
    }
  };

  const copyText = async (value) => {
    const v = String(value || '').trim();
    if (!v) return;
    try {
      await navigator.clipboard.writeText(v);
      toast.success('Скопировано');
    } catch {
      toast.success(v);
    }
  };

  useEffect(() => {
    if (!isQrLoginOpen) {
      stopQrPolling();
      setQrLogin(null);
      setQrStatus('idle');
      setQrError(null);
      return () => {};
    }
    startQrLogin();
    return () => stopQrPolling();
  }, [isQrLoginOpen]);

  useEffect(() => {
    if (!isQrLoginOpen) return () => {};
    const expiresAt = typeof qrLogin?.expires_at === 'string' ? qrLogin.expires_at : '';
    if (!expiresAt) return () => {};
    const expiresMs = new Date(expiresAt).getTime();
    if (!Number.isFinite(expiresMs)) return () => {};
    const refreshInMs = Math.max(0, expiresMs - Date.now() - 3000);
    if (qrRefreshTimerRef.current) {
      window.clearTimeout(qrRefreshTimerRef.current);
      qrRefreshTimerRef.current = null;
    }
    qrRefreshTimerRef.current = window.setTimeout(() => {
      startQrLogin();
    }, refreshInMs);
    if (qrCountdownTimerRef.current) {
      window.clearInterval(qrCountdownTimerRef.current);
      qrCountdownTimerRef.current = null;
    }
    const computeSeconds = () => {
      const remaining = Math.max(0, Math.ceil((expiresMs - Date.now()) / 1000));
      setQrRefreshInSeconds(remaining);
    };
    computeSeconds();
    qrCountdownTimerRef.current = window.setInterval(computeSeconds, 500);
    return () => {
      if (qrRefreshTimerRef.current) {
        window.clearTimeout(qrRefreshTimerRef.current);
        qrRefreshTimerRef.current = null;
      }
      if (qrCountdownTimerRef.current) {
        window.clearInterval(qrCountdownTimerRef.current);
        qrCountdownTimerRef.current = null;
      }
    };
  }, [isQrLoginOpen, qrLogin?.expires_at]);

  useEffect(() => {
    if (!isQrLoginOpen) return () => {};
    const qrId = String(qrLogin?.qr_id || '').trim();
    const code = String(qrLogin?.code || '').trim();
    if (!qrId || !code) return () => {};
    if (qrStatus !== 'pending') return () => {};

    let isActive = true;

    const poll = async () => {
      try {
        const resp = await axios.post(`${API}/auth/qr/poll`, { qr_id: qrId, code }, { withCredentials: true });
        if (!isActive) return;
        const status = String(resp?.data?.status || '');
        if (status === 'issued') {
          stopQrPolling();
          setQrStatus('issued');
          setIsQrLoginOpen(false);
          await onLogin?.();
          toast.success('Успешный вход!');
        }
      } catch (error) {
        if (!isActive) return;
        const status = error?.response?.status;
        if (status === 410) {
          stopQrPolling();
          startQrLogin();
          return;
        }
        setQrStatus('error');
        setQrError(error?.response?.data?.detail || 'Не удалось выполнить вход по QR');
      }
    };

    poll();
    pollTimerRef.current = window.setInterval(poll, 1200);

    return () => {
      isActive = false;
      stopQrPolling();
    };
  }, [isQrLoginOpen, onLogin, qrLogin?.code, qrLogin?.qr_id, qrStatus]);

  return (
    <div className={embedded ? 'w-full' : 'min-h-[100dvh] flex items-center justify-center ui-page-bg p-4 sm:p-6'}>
      {!embedded ? (
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30"></div>
      ) : null}
      <div className={embedded ? 'w-full max-w-md' : 'w-full max-w-md relative z-10 animate-fade-in'}>
        {!embedded ? (
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 mb-4 shadow-lg shadow-emerald-500/20">
              <Terminal className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent mb-2">SSH Terminal</h1>
            <p className="text-white/80">Управляйте серверами из браузера</p>
          </div>
        ) : null}

        <Card className={cn('glass border-white/10 shadow-2xl', embedded ? 'animate-fade-in' : '')}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-white/5">
              <TabsTrigger value="login" data-testid="login-tab">Вход</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Регистрация</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <CardHeader>
                <CardTitle className="text-white">Войти в систему</CardTitle>
                <CardDescription>Введите данные для входа</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={twoFaLogin ? handleLoginTwoFa : handleLogin} className="space-y-4" autoComplete="off">
                  <div className="space-y-2">
                    <Label htmlFor="login-username" className="text-white">Имя пользователя</Label>
                    <Input
                      id="login-username"
                      data-testid="login-username-input"
                      type="text"
                      value={loginData.username}
                      onChange={(e) => {
                        setShowForgotHint(false);
                        setLoginData({ ...loginData, username: e.target.value });
                      }}
                      autoComplete="off"
                      required
                      className="bg-black/10 border-white/10"
                      disabled={Boolean(twoFaLogin)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-white">Пароль</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        data-testid="login-password-input"
                        type={showLoginPassword ? 'text' : 'password'}
                        value={loginData.password}
                        onChange={(e) => {
                          setShowForgotHint(false);
                          setLoginData({ ...loginData, password: e.target.value });
                        }}
                        autoComplete="new-password"
                        required
                        className="bg-black/10 border-white/10 pr-10"
                        disabled={Boolean(twoFaLogin)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowLoginPassword((v) => !v)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
                        aria-label={showLoginPassword ? 'Скрыть пароль' : 'Показать пароль'}
                        disabled={Boolean(twoFaLogin)}
                      >
                        {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    {!twoFaLogin && showForgotHint ? (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          className="text-sm text-emerald-300/90 hover:text-emerald-200 underline underline-offset-4"
                          onClick={() => {
                            setForgotEmail('');
                            setIsForgotOpen(true);
                          }}
                        >
                          Забыли пароль?
                        </button>
                      </div>
                    ) : null}
                  </div>
                  {twoFaLogin && (
                    <div className="space-y-2">
                      <div className="text-xs text-white/70">
                        Введите код из приложения или резервный код.
                      </div>
                      <Label htmlFor="login-2fa-code" className="text-white">Код 2FA</Label>
                      <Input
                        id="login-2fa-code"
                        data-testid="login-2fa-code-input"
                        value={twoFaCode}
                        onChange={(e) => setTwoFaCode(e.target.value)}
                        autoComplete="off"
                        className="bg-black/10 border-white/10"
                        placeholder="123456 или ABCDE-12345"
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="border-white/10 bg-white/5 text-white/90 hover:bg-white/10"
                          onClick={resetTwoFaLogin}
                          disabled={isTwoFaVerifying}
                        >
                          Назад
                        </Button>
                      </div>
                    </div>
                  )}
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                    disabled={isLoading || isTwoFaVerifying}
                    data-testid="login-submit-button"
                  >
                    {twoFaLogin ? (isTwoFaVerifying ? 'Проверка...' : 'Подтвердить') : (isLoading ? 'Вход...' : 'Войти')}
                  </Button>
                </form>

                <Dialog open={isForgotOpen} onOpenChange={setIsForgotOpen}>
                  <DialogContent className="border-white/10 bg-[#0b1020] text-white">
                    <DialogHeader>
                      <DialogTitle>Восстановление пароля</DialogTitle>
                      <DialogDescription>
                        Введите email. Мы отправим новый пароль на почту.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="forgot-email" className="text-white">Email</Label>
                        <Input
                          id="forgot-email"
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          className="bg-black/10 border-white/10"
                        />
                      </div>
                      <Button
                        type="button"
                        className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                        onClick={sendForgotPassword}
                        disabled={isForgotSending}
                      >
                        {isForgotSending ? 'Отправка...' : 'Отправить'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <div className="mt-4 space-y-2">
                  <Dialog open={isQrLoginOpen} onOpenChange={setIsQrLoginOpen}>
                    <DialogTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full border-white/10 bg-white/5 text-white/90 hover:bg-white/10"
                        disabled={isLoading || isTwoFaVerifying}
                      >
                        <QrCode className="w-4 h-4 mr-2" />
                        Войти по QR
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="text-white max-h-[85dvh] flex flex-col">
                      <DialogHeader>
                        <DialogTitle className="text-white">Вход по QR</DialogTitle>
                        <DialogDescription className="text-white/70">
                          Откройте «Сессии» на устройстве, где вы уже вошли, и подтвердите вход.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3">
                        {qrError ? <div className="text-xs text-red-300">{qrError}</div> : null}
                        <div className="rounded-xl border border-white/10 bg-black/30 p-4 flex flex-col items-center gap-3">
                          {qrLogin?.data_url ? (
                            <img src={qrLogin.data_url} alt="QR" className="w-[260px] h-[260px] rounded-lg bg-black/30" />
                          ) : (
                            <div className="w-[260px] h-[260px] rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-sm text-white/70">
                              {qrStatus === 'starting' ? 'Создание...' : '—'}
                            </div>
                          )}
                          {qrLogin?.code ? (
                            <div className="w-full text-xs text-white/80 flex flex-col items-center gap-2">
                              <div className="font-mono break-all text-center">{qrLogin.code}</div>
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="border-white/10 bg-white/5 text-white/90 hover:bg-white/10"
                                  onClick={() => copyText(qrLogin.code)}
                                >
                                  Скопировать код
                                </Button>
                                {qrLogin?.url ? (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    className="border-white/10 bg-white/5 text-white/90 hover:bg-white/10"
                                    onClick={() => copyText(qrLogin.url)}
                                  >
                                    Скопировать ссылку
                                  </Button>
                                ) : null}
                              </div>
                            </div>
                          ) : null}
                          <div className="text-xs text-white/70 text-center">
                            {qrStatus === 'pending' ? 'Ожидание подтверждения...' : null}
                            {qrStatus === 'issued' ? 'Вход выполнен' : null}
                            {qrStatus === 'pending' && Number.isFinite(qrRefreshInSeconds) ? (
                              <div className="mt-1 text-white/50">Обновится через {Math.max(0, qrRefreshInSeconds)}с</div>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            className="border-white/10 bg-white/5 text-white/90 hover:bg-white/10"
                            onClick={() => setIsQrLoginOpen(false)}
                          >
                            Закрыть
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-white/10 bg-white/5 text-white/90 hover:bg-white/10"
                    onClick={() => setIsQrScanOpen(true)}
                    disabled={isLoading || isTwoFaVerifying || isQrScanExchanging}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Сканировать QR (войти)
                  </Button>
                  <QrScannerDialog
                    open={isQrScanOpen}
                    onOpenChange={setIsQrScanOpen}
                    title="Сканировать QR для входа"
                    description="Отсканируйте QR, который отображается на ПК (в окне сессий). Можно вставить значение вручную."
                    confirmLabel="Войти"
                    onResult={handleScanLoginResult}
                  />
                </div>
              </CardContent>
            </TabsContent>

            <TabsContent value="register">
              <CardHeader>
                <CardTitle className="text-white">Создать аккаунт</CardTitle>
                <CardDescription>Зарегистрируйтесь для начала работы</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4" autoComplete="off">
                  <div className="space-y-2">
                    <Label htmlFor="register-username" className="text-white">Имя пользователя</Label>
                    <Input
                      id="register-username"
                      data-testid="register-username-input"
                      type="text"
                      value={registerData.username}
                      onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                      autoComplete="off"
                      required
                      className="bg-black/10 border-white/10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-white">Пароль</Label>
                    <div className="relative">
                      <Input
                        id="register-password"
                        data-testid="register-password-input"
                        type={showRegisterPassword ? 'text' : 'password'}
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        autoComplete="new-password"
                        required
                        className="bg-black/10 border-white/10 pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowRegisterPassword((v) => !v)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
                        aria-label={showRegisterPassword ? 'Скрыть пароль' : 'Показать пароль'}
                      >
                        {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    <div className="text-xs text-white/70">
                      Минимум 8 символов, обязательно: 1 заглавная (A-Z), 1 строчная (a-z), 1 цифра (0-9), 1 спецсимвол.
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password-confirm" className="text-white">Повторите пароль</Label>
                    <div className="relative">
                      <Input
                        id="register-password-confirm"
                        data-testid="register-password-confirm-input"
                        type={showRegisterPassword ? 'text' : 'password'}
                        value={registerData.passwordConfirm}
                        onChange={(e) => setRegisterData({ ...registerData, passwordConfirm: e.target.value })}
                        autoComplete="new-password"
                        required
                        className={cn(
                          'bg-black/10 border-white/10 pr-10',
                          registerData.passwordConfirm &&
                            registerData.password !== registerData.passwordConfirm &&
                            'border-red-500'
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowRegisterPassword((v) => !v)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
                        aria-label={showRegisterPassword ? 'Скрыть пароль' : 'Показать пароль'}
                      >
                        {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    {registerData.passwordConfirm && registerData.password !== registerData.passwordConfirm && (
                      <div className="text-xs text-red-400">Пароли не совпадают</div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-white">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      autoComplete="email"
                      required
                      inputMode="email"
                      className={cn(
                        'bg-black/10 border-white/10',
                        registerData.email && (!EMAIL_RE.test(String(registerData.email || '').trim()) || String(registerData.email || '').trim().length > 254) && 'border-red-500'
                      )}
                      placeholder="you@example.com"
                    />
                    {registerData.email &&
                      (!EMAIL_RE.test(String(registerData.email || '').trim()) || String(registerData.email || '').trim().length > 254) && (
                        <div className="text-xs text-red-400">Некорректный email</div>
                      )}
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                    disabled={isLoading}
                    data-testid="register-submit-button"
                  >
                    {isLoading ? 'Создание...' : 'Зарегистрироваться'}
                  </Button>
                </form>
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
